import { supabase } from "@/integrations/supabase/client";

export interface TurnstileVerificationResult {
  success: boolean;
  error?: string;
  errorCodes?: string[];
}

/**
 * Verifica o token do Turnstile no servidor
 */
export async function verifyTurnstileToken(token: string): Promise<TurnstileVerificationResult> {
  try {
    const { data, error } = await supabase.functions.invoke("verify-turnstile", {
      body: { token },
    });

    if (error) {
      console.error("Turnstile verification error:", error);
      return {
        success: false,
        error: error.message || "Erro ao verificar token",
      };
    }

    if (!data || !data.success) {
      return {
        success: false,
        error: data?.error || "Verificação falhou. Tente novamente.",
        errorCodes: data?.errorCodes,
      };
    }

    return { success: true };
  } catch (error) {
    console.error("Turnstile verification error:", error);
    return {
      success: false,
      error: "Erro ao verificar token. Tente novamente.",
    };
  }
}

/**
 * Carrega o script do Turnstile dinamicamente
 */
export function loadTurnstileScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    // Verificar se o script já foi carregado e se window.turnstile está disponível
    if (document.querySelector('script[src="https://challenges.cloudflare.com/turnstile/v0/api.js"]') && window.turnstile) {
      resolve();
      return;
    }

    // Se o script existe mas window.turnstile não está disponível, aguardar
    if (document.querySelector('script[src="https://challenges.cloudflare.com/turnstile/v0/api.js"]')) {
      const checkInterval = setInterval(() => {
        if (window.turnstile) {
          clearInterval(checkInterval);
          resolve();
        }
      }, 100);

      // Timeout após 5 segundos
      setTimeout(() => {
        clearInterval(checkInterval);
        if (!window.turnstile) {
          reject(new Error("Turnstile script loaded but API not available"));
        }
      }, 5000);
      return;
    }

    const script = document.createElement("script");
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js";
    script.async = true;
    script.defer = true;
    script.onload = () => {
      // Aguardar um pouco para garantir que window.turnstile está disponível
      setTimeout(() => {
        if (window.turnstile) {
          resolve();
        } else {
          reject(new Error("Turnstile script loaded but API not available"));
        }
      }, 100);
    };
    script.onerror = () => reject(new Error("Failed to load Turnstile script"));
    document.head.appendChild(script);
  });
}

/**
 * Obtém o site key do Turnstile das variáveis de ambiente ou usa fallback
 */
export function getTurnstileSiteKey(): string {
  // Tentar pegar da variável de ambiente primeiro
  const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY;
  
  // Fallback hardcoded se a variável não estiver configurada
  const FALLBACK_SITE_KEY = "0x4AAAAAAClwI5GaNw1NlCeP";
  
  const finalSiteKey = siteKey || FALLBACK_SITE_KEY;
  
  if (!siteKey) {
    // Só mostrar avisos em desenvolvimento
    if (import.meta.env.DEV) {
      console.warn("⚠️ VITE_TURNSTILE_SITE_KEY não configurada, usando fallback");
    }
  } else {
    // Só logar em desenvolvimento para não poluir o console em produção
    if (import.meta.env.DEV) {
      console.log("✅ VITE_TURNSTILE_SITE_KEY configurada:", siteKey.substring(0, 10) + "...");
    }
  }
  
  return finalSiteKey;
}
